import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';

const DOS = () => {
    const { domain } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [data, setData] = useState(null);
    const [monitorLoading, setMonitorLoading] = useState(true);
    const [monitorError, setMonitorError] = useState(null);

    // Settings State
    const [settings, setSettings] = useState({
        api_key: '',
        expiry: 'unlimited',
        contact_email: '',
        view_preferences: {
            connections: true,
            cpu: true,
            ram: true,
            disk: true,
            source_ips: true,
            processes: true,
            file_mods: true,
            ports: true,
            logs: true
        }
    });
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const triggerToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // Fetch Monitor Data
    useEffect(() => {
        if (settingsLoading) return; // Wait for settings to fetch the correct key

        const fetchMonitorData = async () => {
            if (!domain) return;

            try {
                // Construct the exact endpoint based on individual setting API key
                const key = settings.api_key || '1';
                const wpPath = `/wp-admin/admin-ajax.php?action=secure360_monitor_api&key=${key}`;

                let response;
                let fetchedData = null;

                try {
                    // Try WordPress Plugin First
                    response = await api.get('/api/dos/ram', {
                        params: {
                            target: `https://${domain}`,
                            endpoint: btoa(wpPath)
                        }
                    });
                    fetchedData = response.data;
                } catch (wpErr) {
                    // Fallback to standalone script collector
                    response = await api.get('/api/dos/ram', {
                        params: {
                            target: `https://${domain}`,
                            endpoint: btoa('/collector.php')
                        }
                    });
                    fetchedData = Array.isArray(response.data) ? response.data[0] : response.data;
                }

                if (fetchedData && (fetchedData.ram || fetchedData.uptime)) {
                    setData(fetchedData);
                    setMonitorError(null);
                } else {
                    throw new Error("Invalid data format");
                }
            } catch (err) {
                console.error("Monitor fetch failed:", err);
                if (err.response && err.response.data && err.response.data.error) {
                    setMonitorError(err.response.data.error || "Plugin Not Installed");
                } else {
                    setMonitorError("Plugin Not Installed");
                }
            } finally {
                setMonitorLoading(false);
            }
        };

        fetchMonitorData();
        const interval = setInterval(fetchMonitorData, 30000); // 30s refresh
        return () => clearInterval(interval);
    }, [domain, settings.api_key, settingsLoading]);

    // Fetch Settings
    useEffect(() => {
        const fetchSettings = async () => {
            if (!domain) return;
            try {
                const res = await api.get(`/api/dos/settings`, { params: { domain } });
                if (res.data) {
                    setSettings(prev => ({
                        ...prev,
                        ...res.data,
                        view_preferences: { ...prev.view_preferences, ...(res.data.view_preferences || {}) }
                    }));
                }
            } catch (err) {
                console.error("Failed to fetch settings:", err);
            } finally {
                setSettingsLoading(false);
            }
        };
        fetchSettings();
    }, [domain]);

    const handleGenerateKey = async () => {
        if (settings.api_key && !window.confirm("Are you sure you want to generate a new key? The existing key will stop working immediately.")) {
            return;
        }
        try {
            setSaving(true);
            const res = await api.post('/api/dos/generate-key', { domain });
            if (res.data.api_key) {
                setSettings(prev => ({ ...prev, api_key: res.data.api_key }));
                triggerToast("New Key Generated!", "success");
            }
        } catch (err) {
            triggerToast("Failed to generate key", "danger");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            await api.post('/api/dos/settings', {
                domain,
                contact_email: settings.contact_email,
                expiry: settings.expiry,
                view_preferences: settings.view_preferences
            });
            triggerToast("Settings Saved Successfully!", "success");
        } catch (err) {
            triggerToast("Failed to save settings", "danger");
        } finally {
            setSaving(false);
        }
    };

    const togglePreference = (key) => {
        setSettings(prev => ({
            ...prev,
            view_preferences: {
                ...prev.view_preferences,
                [key]: !prev.view_preferences[key]
            }
        }));
    };

    const handleCopyKey = () => {
        if (!settings.api_key) {
            triggerToast("No key to copy!", "warning");
            return;
        }
        navigator.clipboard.writeText(settings.api_key);
        triggerToast("Key copied to clipboard!", "success");
    };

    // Render Logic relative to Tab
    if (!domain) return null;

    return (
        <PageLayout title={`Secure360: ${domain}`} className="bg-light min-vh-100 font-monospace">
            {/* Custom Toast Notification */}
            {toast.show && (
                <div
                    className={`alert alert-${toast.type} shadow-lg border-0 d-flex align-items-center`}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        zIndex: 9999,
                        minWidth: '300px',
                        animation: 'slideIn 0.3s ease-out'
                    }}
                >
                    <i className={`las ${toast.type === 'success' ? 'la-check-circle' : 'la-exclamation-circle'} fs-4 me-2`}></i>
                    <div>{toast.message}</div>
                    <button type="button" className="btn-close ms-auto" onClick={() => setToast({ ...toast, show: false })}></button>
                </div>
            )}

            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>

            {/* Header & Tabs */}
            <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded shadow-sm border">
                <h5 className="mb-0 d-flex align-items-center text-dark fw-bold">
                    <i className="las la-shield-alt text-primary me-2 fs-4"></i> Secure360: <span className="ms-2 text-muted small">{domain}</span>
                </h5>
                <div className="d-flex gap-2">
                    <button
                        className={`btn btn-sm ${activeTab === 'dashboard' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        <i className="las la-tachometer-alt me-1"></i> Dashboard
                    </button>
                    <button
                        className={`btn btn-sm ${activeTab === 'settings' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <i className="las la-cog me-1"></i> Settings
                    </button>
                    <button className="btn btn-outline-dark btn-sm ms-2" onClick={() => navigate('/scans')}>Back</button>
                </div>
            </div>

            {/* Content Switch */}
            {activeTab === 'dashboard' ? (
                <DashboardView
                    loading={monitorLoading}
                    error={monitorError}
                    data={data}
                    preferences={settings.view_preferences}
                    domain={domain}
                    setActiveTab={setActiveTab}
                />
            ) : (
                <SettingsView
                    settings={settings}
                    setSettings={setSettings}
                    onGenerateKey={handleGenerateKey}
                    onCopyKey={handleCopyKey}
                    onSave={handleSaveSettings}
                    onTogglePref={togglePreference}
                    saving={saving}
                />
            )}
        </PageLayout>
    );
};

// Sub-components

const SettingsView = ({ settings, setSettings, onGenerateKey, onCopyKey, onSave, onTogglePref, saving }) => {
    return (
        <div className="row justify-content-center">
            <div className="col-lg-8">
                <div className="card shadow-sm border">
                    <div className="card-header bg-white fw-bold py-3">
                        <i className="las la-sliders-h me-2"></i> Monitor Settings
                    </div>
                    <div className="card-body p-4">

                        {/* API Key Section */}
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="fw-bold mb-3 text-primary">API Access Key</h6>
                            <div className="input-group mb-3">
                                <input
                                    type="text"
                                    className="form-control font-monospace"
                                    value={settings.api_key || "No Key Generated"}
                                    readOnly
                                />
                                <button className="btn btn-outline-secondary" type="button" onClick={onCopyKey} title="Copy Key">
                                    <i className="las la-copy"></i>
                                </button>
                                <button className="btn btn-outline-primary" type="button" onClick={onGenerateKey} disabled={saving}>
                                    <i className="las la-key me-2"></i> Generate KEY
                                </button>
                            </div>
                            <small className="text-muted">
                                This key must be entered in the WordPress plugin allowed keys list. Auto-saved upon generation.
                            </small>
                        </div>

                        {/* Notifications & Expiry */}
                        <div className="row mb-4 pb-4 border-bottom">
                            <div className="col-md-6">
                                <h6 className="fw-bold mb-3">Notification Email</h6>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="admin@example.com"
                                    value={settings.contact_email || ''}
                                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                                />
                                <div className="form-text">Alerts will be sent here in case of emergency.</div>
                            </div>
                            <div className="col-md-6">
                                <h6 className="fw-bold mb-3">Key Usage Expiry</h6>
                                <div className="d-flex gap-3 mt-2">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="expiry"
                                            id="expUnlimited"
                                            checked={settings.expiry === 'unlimited'}
                                            onChange={() => setSettings({ ...settings, expiry: 'unlimited' })}
                                        />
                                        <label className="form-check-label" htmlFor="expUnlimited">Unlimited</label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="expiry"
                                            id="expCustom"
                                            checked={settings.expiry !== 'unlimited'}
                                            onChange={() => setSettings({ ...settings, expiry: new Date().toISOString().split('T')[0] })}
                                        />
                                        <label className="form-check-label" htmlFor="expCustom">Custom Date</label>
                                    </div>
                                </div>
                                {settings.expiry !== 'unlimited' && (
                                    <input
                                        type="date"
                                        className="form-control mt-2"
                                        value={settings.expiry || ''}
                                        onChange={(e) => setSettings({ ...settings, expiry: e.target.value })}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Dashboard Visibility */}
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3">Dashboard Visibility Control</h6>
                            <p className="text-muted small">Select which sections are visible on the dashboard.</p>
                            <div className="row g-3">
                                {Object.keys(settings.view_preferences || {}).map(key => (
                                    <div className="col-md-4" key={key}>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`pref_${key}`}
                                                checked={settings.view_preferences[key]}
                                                onChange={() => onTogglePref(key)}
                                            />
                                            <label className="form-check-label text-capitalize" htmlFor={`pref_${key}`}>
                                                {key.replace('_', ' ')}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="d-flex justify-content-end pt-3 border-top">
                            <button className="btn btn-success px-4" onClick={onSave} disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : <><i className="las la-save me-2"></i>Save Settings</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DashboardView = ({ loading, error, data, preferences, domain, setActiveTab }) => {
    const processRows = useMemo(() => {
        const processes = data?.sys?.processes;
        if (!processes || processes === 'Shell Disabled') return [];
        const lines = typeof processes === 'string' ? processes.trim().split('\n') : [];
        const contentLines = lines.filter(l => !l.match(/PID|USER|COMMAND/i));
        return contentLines.map((line, idx) => {
            const parts = line.trim().split(/\s+/);
            if (parts.length < 4) return null;
            let pid = parts[0], user = parts[1], cpu = parts[2] && parts[2].includes('%') ? parts[2] : `${parts[2]}%`, cmd = parts.slice(4).join(' ');
            if (isNaN(parts[0]) && !isNaN(parts[1])) {
                pid = parts[1]; user = parts[0]; cpu = `${parts[2]}%`; cmd = parts.slice(10).join(' ');
            }
            return { id: idx, pid, user, cpu, cmd };
        }).filter(Boolean);
    }, [data]);

    const ipsPag = useClientPagination(data?.net?.top_ips || [], `${domain}-ips`);
    const filesPag = useClientPagination(Array.isArray(data?.sys?.recent_files) ? data.sys.recent_files : [], `${domain}-files`);
    const portsPag = useClientPagination(Array.isArray(data?.net?.listening) ? data.net.listening : [], `${domain}-ports`);
    const floodPag = useClientPagination(data?.logs?.flood || [], `${domain}-flood`);
    const uaPag = useClientPagination(data?.logs?.ua || [], `${domain}-ua`);
    const procPag = useClientPagination(processRows, `${domain}-proc`);

    // Helper to get color based on percentage
    const getProgressBarColor = (percent) => {
        if (percent >= 80) return 'bg-danger';
        if (percent <= 20) return 'bg-warning';
        return 'bg-success';
    };

    // Render Helpers
    const renderEmptyRow = (message, colSpan = 3) => (
        <tr><td colSpan={colSpan} className="text-center text-muted p-3">{message}</td></tr>
    );

    if (loading && !data && !error) {
        return (
            <div className="d-flex justify-content-center align-items-center min-h-[50vh]">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3"></div>
                    <p className="text-muted">Connecting to {domain}...</p>
                </div>
            </div>
        );
    }

    if (error) {
        const isExpired = error === 'Access Expired';
        const showTitle = isExpired ? 'Access Expired' : 'Plugin Not Installed';

        return (
            <div className="d-flex align-items-center justify-content-center min-h-[50vh]">
                <div className="text-center p-5">
                    <i className={`las ${isExpired ? 'la-hourglass-end text-danger' : 'la-plug text-warning'} text-[5rem]`}></i>
                    <h2 className="text-dark mt-4 mb-3">{showTitle}</h2>
                    <p className="lead mb-4 text-muted">
                        {isExpired
                            ? "Your monitoring subscription for this domain has expired. Please update settings."
                            : (error === "Plugin Not Installed" ? "Unable to retrieve data. Please check connection or generate a key." : error)}
                    </p>
                    <button onClick={() => window.location.reload()} className="btn btn-primary"><i className="las la-sync me-2"></i>Retry</button>
                    {!isExpired && (
                        <button onClick={() => setActiveTab('settings')} className="btn btn-outline-primary ms-2">
                            <i className="las la-cog me-2"></i>Check Settings
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (!data) return null;

    const safePrefs = preferences || {};

    return (
        <div>
            {/* Info Bar */}
            <div className="alert alert-light border d-flex justify-content-between py-1 px-3 mb-3 bg-white shadow-sm x-small text-[0.75rem]">
                <span>
                    <i className="las la-file-alt text-muted me-1"></i>
                    <strong>Log:</strong> <span className="text-primary font-monospace ms-1">{data.path_access || 'Checking...'}</span>
                </span>
                <span>
                    <i className="las la-terminal text-muted me-1"></i>
                    <strong>Shell:</strong> <span className="ms-1">{data.has_shell ? "Yes" : "No"}</span>
                </span>
            </div>

            {/* Security Alert Box */}
            <div className={`alert ${data.security?.alert ? 'alert-danger' : 'd-none'} shadow-sm`} role="alert">
                <strong>CRITICAL WARNING:</strong> {data.security?.message}
            </div>

            {/* Metrics Row */}
            <div className="row g-3 mb-4">
                {safePrefs.connections && (
                    <div className="col-xl-3 col-md-6">
                        <div className="card text-center h-100 border shadow-sm">
                            <div className="card-body p-3">
                                <h6 className="card-title text-muted text-uppercase small ls-1 mb-2">Connections</h6>
                                <div className="h3 fw-bold mb-2 text-dark">{data.net?.established || 0}</div>
                                <div className="d-flex justify-content-center gap-2 small">
                                    <span className="badge bg-warning text-dark">{data.net?.syn_recv || 0} SYN</span>
                                    <span className="badge bg-info text-dark">{data.net?.time_wait || 0} WAIT</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {safePrefs.cpu && (
                    <div className="col-xl-3 col-md-6">
                        <div className="card text-center h-100 border shadow-sm">
                            <div className="card-body p-3">
                                <h6 className="card-title text-muted text-uppercase small mb-2 ls-1">CPU Load (1m)</h6>
                                <div className="h3 fw-bold mb-2 text-dark">
                                    {data.cpu?.load ? data.cpu.load[0].toFixed(2) : '--'}
                                </div>
                                <div className="progress mt-3 h-[6px]">
                                    <div className={`progress-bar ${getProgressBarColor(data.cpu?.percent || 0)}`} style={{ width: `${data.cpu?.percent || 0}%` }}></div>
                                </div>
                                <div className="small text-muted mt-2 text-truncate" title={data.cpu?.model}>
                                    {typeof data.cpu?.model === 'string' ? data.cpu?.model?.split('@')[0] : data.cpu?.model}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {safePrefs.ram && (
                    <div className="col-xl-3 col-md-6">
                        <div className="card text-center h-100 border shadow-sm">
                            <div className="card-body p-3">
                                <h6 className="card-title text-muted text-uppercase small mb-2 ls-1">RAM Usage</h6>
                                <div className="h3 fw-bold mb-2 text-dark">{data.ram?.percent || 0}%</div>
                                <div className="progress mt-3 h-[6px]">
                                    <div className={`progress-bar ${getProgressBarColor(data.ram?.percent || 0)}`} style={{ width: `${data.ram?.percent || 0}%` }}></div>
                                </div>
                                <div className="small text-muted mt-2">
                                    {Math.round(data.ram?.used || 0)} / {Math.round(data.ram?.total || 1)} MB
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {safePrefs.disk && (
                    <div className="col-xl-3 col-md-6">
                        <div className="card text-center h-100 border shadow-sm">
                            <div className="card-body p-3">
                                <h6 className="card-title text-muted text-uppercase small mb-2 ls-1">Disk Usage</h6>
                                <div className="h3 fw-bold mb-2 text-dark">{data.disk?.percent || 0}%</div>
                                <div className="progress mt-3 h-[6px]">
                                    <div className={`progress-bar ${getProgressBarColor(data.disk?.percent || 0)}`} style={{ width: `${data.disk?.percent || 0}%` }}></div>
                                </div>
                                <div className="small text-muted mt-2">Local Storage</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content Grid */}
            <div className="row g-3">
                <div className="col-lg-6">
                    {safePrefs.source_ips && (
                        <div className="card mb-3 shadow-sm border">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                                <span className="fw-bold"><i className="las la-globe-americas text-secondary me-2"></i> Source IPs</span>
                                <span className="badge bg-light text-dark border">Netstat</span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-hover table-sm mb-0 small">
                                    <thead className="table-light"><tr><th>#</th><th>IP Address</th></tr></thead>
                                    <tbody>
                                        {ipsPag.items.length > 0 ? (
                                            ipsPag.items.map((ip, i) => (
                                                <tr key={i}>
                                                    <td width="50"><span className="badge bg-secondary">{typeof ip === 'object' ? ip.count : ''}</span></td>
                                                    <td className="font-monospace text-primary">{typeof ip === 'object' ? ip.ip : ip}</td>
                                                </tr>
                                            ))
                                        ) : renderEmptyRow("No active remote connections", 2)}
                                    </tbody>
                                </table>
                                <TablePagination {...ipsPag} onPageChange={ipsPag.setCurrentPage} className="px-3 pb-2" />
                            </div>
                        </div>
                    )}

                    {safePrefs.processes && (
                        <div className="card shadow-sm border mb-3">
                            <div className="card-header bg-white fw-bold py-2">
                                <i className="las la-microchip text-secondary me-2"></i> Top Processes
                            </div>
                            <div className="card-body p-0 table-responsive">
                                <table className="table table-hover table-sm mb-0 small font-monospace">
                                    <thead className="table-light"><tr><th>PID</th><th>Use</th><th>%</th><th>CMD</th></tr></thead>
                                    <tbody>
                                        {procPag.items.length > 0 ? procPag.items.map((row) => (
                                            <tr key={row.id}>
                                                <td><span className="badge bg-light text-dark border">{row.pid}</span></td>
                                                <td>{row.user}</td>
                                                <td>{row.cpu}</td>
                                                <td className="text-truncate max-w-[100px]" title={row.cmd}>{row.cmd}</td>
                                            </tr>
                                        )) : renderEmptyRow("Unavailable (shell_exec failed or empty)", 4)}
                                    </tbody>
                                </table>
                                <TablePagination {...procPag} onPageChange={procPag.setCurrentPage} className="px-3 pb-2" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-lg-6">
                    {safePrefs.file_mods && (
                        <div className="card mb-3 shadow-sm border">
                            <div className="card-header bg-white text-danger fw-bold py-2">
                                <i className="las la-file-code me-2"></i> File Mods (1h)
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-hover table-sm mb-0 small">
                                    <thead className="table-light"><tr><th>Time</th><th>User</th><th>File</th></tr></thead>
                                    <tbody>
                                        {filesPag.items.length > 0 ? (
                                            filesPag.items.map((f, i) => (
                                                <tr key={i}>
                                                    <td>{f.time}</td>
                                                    <td>{f.user}</td>
                                                    <td className="text-truncate max-w-[100px]" title={f.path}>{f.path}</td>
                                                </tr>
                                            ))
                                        ) : renderEmptyRow("No recently modified .php files")}
                                    </tbody>
                                </table>
                                <TablePagination {...filesPag} onPageChange={filesPag.setCurrentPage} className="px-3 pb-2" />
                            </div>
                        </div>
                    )}

                    {safePrefs.ports && (
                        <div className="card shadow-sm border mb-3">
                            <div className="card-header bg-white fw-bold py-2">
                                <i className="las la-door-open text-secondary me-2"></i> Listening Ports
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-hover table-sm mb-0 small">
                                    <thead className="table-light"><tr><th>Proto</th><th>Port</th><th>Process</th></tr></thead>
                                    <tbody>
                                        {portsPag.items.length > 0 ? (
                                            portsPag.items.map((p, i) => (
                                                <tr key={i}>
                                                    <td>{p.proto}</td>
                                                    <td>{p.port}</td>
                                                    <td className="text-truncate max-w-[100px]">{p.process}</td>
                                                </tr>
                                            ))
                                        ) : renderEmptyRow("No open ports found")}
                                    </tbody>
                                </table>
                                <TablePagination {...portsPag} onPageChange={portsPag.setCurrentPage} className="px-3 pb-2" />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {safePrefs.logs && (
                <div className="row mt-3">
                    <div className="col-12">
                        <div className="card shadow-sm border">
                            <div className="card-header bg-white fw-bold py-2">
                                <i className="las la-search text-secondary me-2"></i> Advanced Log Analysis
                            </div>
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    <div className="col-md-6 border-end">
                                        <div className="p-2 bg-light border-bottom text-center text-muted small fw-bold">POTENTIAL FLOODERS</div>
                                        <table className="table table-hover table-sm mb-0 small">
                                            <tbody>
                                                {floodPag.items.length > 0 ? (
                                                    floodPag.items.map((item, i) => (
                                                        <tr key={i}>
                                                            <td width="50"><span className="badge bg-secondary">{item.count}</span></td>
                                                            <td className="text-break font-monospace">{item.ip}</td>
                                                        </tr>
                                                    ))
                                                ) : renderEmptyRow("Nothing found", 2)}
                                            </tbody>
                                        </table>
                                        <TablePagination {...floodPag} onPageChange={floodPag.setCurrentPage} className="px-3 pb-2" />
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-2 bg-light border-bottom text-center text-muted small fw-bold">TOP USER AGENTS</div>
                                        <table className="table table-hover table-sm mb-0 small">
                                            <tbody>
                                                {uaPag.items.length > 0 ? (
                                                    uaPag.items.map((item, i) => (
                                                        <tr key={i}>
                                                            <td width="50"><span className="badge bg-secondary">{item.count}</span></td>
                                                            <td className="text-break text-[0.85rem]">{item.ua}</td>
                                                        </tr>
                                                    ))
                                                ) : renderEmptyRow("Nothing found", 2)}
                                            </tbody>
                                        </table>
                                        <TablePagination {...uaPag} onPageChange={uaPag.setCurrentPage} className="px-3 pb-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* JSON Accordion */}
            <div className="accordion mt-5" id="rawDataAccordion">
                <div className="accordion-item shadow-sm border-0">
                    <h2 className="accordion-header">
                        <button className="accordion-button collapsed fw-bold text-muted bg-white" type="button" data-bs-toggle="collapse" data-bs-target="#collapseRawData">
                            <i className="las la-code me-2"></i>View Raw Response Data
                        </button>
                    </h2>
                    <div id="collapseRawData" className="accordion-collapse collapse">
                        <div className="accordion-body bg-light">
                            <pre className="mb-0 small max-h-[400px] overflow-y-auto">
                                <code>{JSON.stringify(data, null, 2)}</code>
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DOS;
